import { arrangeDashboard } from "@repo/shared";
import type { DashboardWidgetKey } from "@repo/shared/types";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { cn } from "@/lib/utils";
import { DashboardWidget } from "./widgets";

// Literal classes so Tailwind sees them.
const COLUMN_SPAN: Record<number, string> = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
};

type DashboardGridProps = {
  widgets: DashboardWidgetKey[];
  target: DashboardTarget;
  userHref: (userId: string) => string;
};

/**
 * Key figures four to a row on top, then sections in a 3-column grid in the
 * designer's order. A section that does not fit the rest of its row starts the
 * next one and the gap stays empty — the same arrangement the Settings preview
 * draws.
 */
export function DashboardGrid({ widgets, target, userHref }: DashboardGridProps) {
  const { keyFigures, sectionRows } = arrangeDashboard(widgets);

  return (
    <div className="space-y-6">
      {keyFigures.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {keyFigures.map((key) => (
            <DashboardWidget key={key} widgetKey={key} target={target} userHref={userHref} />
          ))}
        </div>
      )}
      {sectionRows.map((row) => (
        <div key={row.cells.map((c) => c.key).join("|")} className="grid gap-6 lg:grid-cols-3">
          {row.cells.map((cell) => (
            <div key={cell.key} className={cn("min-w-0", COLUMN_SPAN[cell.span])}>
              <DashboardWidget widgetKey={cell.key} target={target} userHref={userHref} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
